<template>

  <div class="order-log-table-wrap">

    <div class="order-log-table-shell">

      <table class="table table-bordered table-hover order-log-table mb-0">

        <thead>

          <tr>

            <th
              v-for="col in fields"
              :key="col.key"
              :class="col.thClass"
              :style="col.thStyle"
            >
               <b-form-checkbox
                v-if="col.key === 'selectRow'"
                :checked="areAllSelectableChecked"
                :indeterminate="isPartiallySelected"
                :disabled="!selectableItems.length"
                @change="toggleSelectAllOnPage"
              /> <template v-else>{{ col.label }}</template
              >
            </th>

          </tr>

        </thead>

        <tbody v-if="loading">

          <tr>

            <td
              :colspan="columnCount"
              class="text-center text-primary py-4"
            >
               <b-spinner class="align-middle"></b-spinner> <strong class="ml-2">Đang tải...</strong
              >
            </td>

          </tr>

        </tbody>

        <tbody v-else-if="!items.length">

          <tr>

            <td
              :colspan="columnCount"
              class="text-center text-muted py-4"
            >
               Không có dữ liệu
            </td>

          </tr>

        </tbody>

        <tbody
          v-for="(row, idx) in items"
          v-else
          :key="row.externalOrderId || idx"
          class="order-log-group"
        >

          <tr class="order-log-meta-row">

            <td
              :colspan="columnCount"
              class="order-log-meta-cell"
            >

              <div
                class="d-flex flex-wrap align-items-center"
                style="font-size: 0.87rem"
              >
                 <b-badge
                  v-if="row.isExpress"
                  variant="danger"
                  pill
                  class="mr-3 align-middle order-meta-express-badge"
                  v-b-tooltip.hover.bottom
                  title="Đơn giao hàng nhanh (Hỏa tốc)"
                  > Hỏa tốc </b-badge
                > <span class="mr-4 d-flex align-items-center"
                  > <i
                    class="fas fa-store icon-color text-warning mr-2"
                    v-b-tooltip.hover.bottom
                    title="Gian hàng sàn TMĐT"
                  ></i
                  > <span class="mr-1">Tên gian hàng:</span> <span class="font-weight-bold text-dark">{{
                    getShopDisplayName(row)
                  }}</span
                  > </span
                > <span class="mr-4"
                  > Ngày đặt đơn: <span class="text-primary">{{ getOrderPlacedAt(row) }}</span
                  > </span
                > <span
                  v-if="getComplaintInfo(row)"
                  class="mr-4 d-inline-flex align-items-center flex-wrap"
                  > <strong class="mr-1">Khiếu nại:</strong> <router-link
                    v-if="complaintTicketId(row) != null"
                    :to="complaintTicketRoute(row)"
                    class="text-primary mr-2 text-12"
                    > {{ complaintTicketId(row) }}</router-link
                  > <b-badge
                    :variant="getComplaintInfo(row).isClosed ? 'success' : 'warning'"
                    > {{ getComplaintInfo(row).isClosed ? 'Đã đóng' : 'Đang xử lý' }} </b-badge
                  > </span
                > <span
                  v-if="row.returnOrderSnList && row.returnOrderSnList.length"
                  class="mr-4 d-inline-flex align-items-center flex-wrap"
                  > <strong class="mr-1">Đơn trả:</strong> <span
                    v-for="(sn, snIdx) in row.returnOrderSnList"
                    :key="sn"
                    class="d-inline-flex align-items-center"
                    > <a
                      href="javascript:void(0)"
                      class="return-order-sn return-order-link text-12"
                      @click.prevent="
                        $emit('view-return-detail', {
                          returnSn: sn,
                          connectionId: row.marketplaceConnectionId,
                        })
                      "
                      >{{ sn }}</a
                    > <span
                      v-if="snIdx < row.returnOrderSnList.length - 1"
                      class="text-muted mx-1"
                      >,</span
                    > </span
                  > </span
                >
              </div>

            </td>

          </tr>

          <tr
            v-if="getRowSyncErrorMessage(row)"
            class="order-log-sync-error-row"
          >

            <td
              :colspan="columnCount"
              class="order-log-error-cell"
            >

              <div
                class="order-log-sync-banner"
                role="alert"
              >
                 <i class="fas fa-exclamation-triangle order-log-sync-banner__icon" aria-hidden="true" /> <span
                  class="order-log-sync-banner__text"
                >{{ getRowSyncErrorMessage(row) }}</span
                >
              </div>

            </td>

          </tr>

          <tr
            v-if="row.externalOrderId && rowActionErrors[row.externalOrderId]"
            class="order-log-error-row"
          >

            <td
              :colspan="columnCount"
              class="order-log-error-cell"
            >

              <div
                class="order-log-inline-error alert alert-danger mb-0 py-2 px-3 small text-left d-flex align-items-start"
                role="alert"
              >
                 <i class="fas fa-exclamation-circle mt-1 mr-2 flex-shrink-0"></i> <span
                  class="flex-grow-1 text-break pr-2"
                >{{ rowActionErrors[row.externalOrderId] }}</span
                > <button
                  type="button"
                  class="close order-log-error-close flex-shrink-0"
                  aria-label="Đóng"
                  @click="dismissRowError(row)"
                >
                   <span aria-hidden="true">&times;</span>
                </button>
              </div>

            </td>

          </tr>

          <tr class="order-log-data-row">

            <td class="text-center">
               <b-form-checkbox
                :checked="isSelected(row.externalOrderId)"
                :disabled="!canSelectForBatch(row)"
                @change="toggleRowSelection(row, $event)"
              />
            </td>

            <td class="text-center">

              <div>
                 <a
                  v-if="row.orderId"
                  href="javascript:void(0)"
                  class="order-id-link text-primary font-weight-bold"
                  @click.prevent="$emit('view-order-detail', row)"
                  > {{ row.orderId }} </a
                > <span
                  v-else
                  class="text-muted"
                  >-</span
                >
              </div>

            </td>

            <td>

              <div
                v-if="getDisplayOrderItems(row).length > 0"
                class="order-items-cell"
              >

                <div
                  v-for="(orderItem, oidx) in getDisplayOrderItems(row)"
                  :key="orderItem.id || oidx"
                  class="d-flex align-items-start flex-gap-8 order-item-row"
                >
                   <ProductImage
                    :src="getOrderItemImageUrl(row, orderItem, oidx)"
                    class="size-40 object-contain flex-shrink-0"
                  />
                  <div class="flex-grow-1 min-w-0">

                    <div
                      class="product-name-lines mb-1"
                      v-b-tooltip.hover.bottom.noninteractive
                      :title="getProductNamesTooltip(row, orderItem, oidx)"
                    >

                      <p class="m-0 text-12 lh-1-4">
                         <span class="font-weight-bold text-dark">Tên trưng bày:</span> <span
                          class="text-body"
                          >{{ getDisplayNameFromItemList(row, orderItem, oidx) || '—' }}</span
                        >
                      </p>

                      <hr class="product-name-separator" />

                      <p class="m-0 text-12 lh-1-4">
                         <span class="font-weight-bold text-dark">Tên hệ thống:</span> <span
                          class="text-body"
                          >{{ getSystemNameFromOrderItems(row, orderItem, oidx) || '—' }}</span
                        >
                      </p>

                    </div>

                    <p
                      v-if="getSecondaryProductLine(orderItem)"
                      class="m-0 text-primary small font-italic mt-1"
                      v-b-tooltip.hover.bottom.noninteractive
                      :title="getSecondaryProductLine(orderItem)"
                    >
                       {{ getSecondaryProductLine(orderItem) }}
                    </p>

                    <p
                      v-if="getOrderItemInStockQuantity(orderItem) !== null"
                      class="m-0 text-12 mt-1"
                    >
                       <span class="font-weight-bold text-dark">Tồn:</span> <span
                        :class="
                          getOrderItemInStockQuantity(orderItem) > 0
                            ? 'text-success font-weight-bold'
                            : 'text-danger font-weight-bold'
                        "
                        >{{ getOrderItemInStockQuantity(orderItem) }}</span
                      >
                    </p>

                    <p class="m-0 text-12 text-muted mt-1">
                       {{ formatAmount(orderItem.productPrice) }} × {{ orderItem.quantity }} = {{
                        formatAmount(orderItem.totalAmount)
                      }}
                    </p>

                  </div>

                </div>

              </div>
               <span
                v-else
                class="text-muted"
                >-</span
              >
            </td>

            <td>

              <div class="buyer-info-cell">
                 <span
                  v-if="getBuyerName(row)"
                  class="font-weight-bold d-block"
                  >{{ getBuyerName(row) }}</span
                > <span
                  v-if="getBuyerPhone(row)"
                  class="text-muted small d-block"
                  >{{ getBuyerPhone(row) }}</span
                >
                <div
                  v-if="getRecipientShippingAddress(row)"
                  class="recipient-shipping-address mt-2 pt-2"
                >
                   <span class="d-block text-11 font-weight-bold text-dark mb-1"
                    >Địa chỉ nhận</span
                  >
                  <span
                    class="d-block text-12 text-break text-muted lh-1-5"
                  >{{ getRecipientShippingAddress(row) }}</span>
                </div>
                <span
                  v-if="
                    !getBuyerName(row) &&
                      !getBuyerPhone(row) &&
                      !getRecipientShippingAddress(row)
                  "
                  class="text-muted"
                  >-</span
                >
              </div>

            </td>

            <td>

              <ul class="list-unstyled mb-0 order-info-list text-12">

                <li v-if="row.externalOrderId">
                   <span class="font-weight-bold">Mã đơn sàn:</span> <span
                    class="text-primary font-weight-bold ml-1"
                    >{{ row.externalOrderId }}</span
                  >
                </li>

                <li> <span class="font-weight-bold">ĐVVC:</span> {{ getShippingUnit(row) }} </li>

                <li v-if="getTrackingNumber(row)">
                   <span class="font-weight-bold">Mã vận đơn:</span> <span
                    v-b-tooltip.hover.bottom.noninteractive
                    :title="getTrackingNumber(row)"
                    >{{ getTrackingNumber(row) }}</span
                  >
                </li>

              </ul>

              <div
                v-if="getOrderNotesEntries(row).length"
                class="order-system-notes mt-2 pt-2 border-top text-12"
              >
                <span class="d-block font-weight-bold text-dark mb-1">Ghi chú đơn hệ thống</span>
                <ul class="list-unstyled mb-0 order-notes-list">
                  <li
                    v-for="(note, nidx) in getOrderNotesEntries(row)"
                    :key="'sys-note-' + row.id + '-' + nidx"
                    class="mb-1"
                  >
                    <span class="font-weight-bold text-muted">{{ note.label }}:</span>
                    <span class="text-break d-inline">{{ note.text }}</span>
                  </li>
                </ul>
              </div>

            </td>

            <td class="text-center">

              <div class="status-combined-cell">
                 <div
                  v-if="shouldShowSyncSpinner(row)"
                  class="order-log-sync-queue-block d-flex flex-column align-items-center"
                  v-b-tooltip.hover.top.noninteractive
                  title="Đồng bộ trong hàng đợi; cập nhật định kỳ."
                >
                   <b-spinner
                    small
                    :variant="getSyncQueueSpinnerVariant(row)"
                    class="mb-1 order-log-sync-spinner"
                  ></b-spinner>
                  <span class="text-11 order-log-sync-queue-caption">{{
                    getSyncQueueSpinnerCaption(row)
                  }}</span>
                </div><status-badge
                  v-if="row.externalStatus"
                  :status="row.externalStatus"
                  :status-map="externalStatusBadgeMap"
                /> <span
                  v-else
                  class="text-muted small d-block mb-1"
                  >-</span
                > <span
                  v-if="getOrderStatusText(row)"
                  class="d-block"
                  style="width: max-content"
                  v-text="getOrderStatusText(row)"
                  :class="bindClassNameStatus(row)"
                ></span
                > <span
                  v-else
                  class="text-muted small d-block"
                  >-</span
                >                  <span
                  class="order-log-sync-time text-muted small d-block mt-1"
                  v-b-tooltip.hover.top
                  title="Thời điểm đồng bộ hệ thống"
                  >{{ formatDateTime(row.createdAt) }}</span
                >
              </div>

            </td>

            <td class="text-center">

              <div class="text-left text-price payment-summary-cell">

                <p
                  v-if="getOrderTotalAmount(row) != null"
                  class="mb-1 text-right"
                >
                   Tổng đơn: <span class="font-weight-bold">{{
                    formatAmount(getOrderTotalAmount(row))
                  }}</span
                  >
                </p>

                <p
                  v-if="getBuyerPaidAmount(row) != null"
                  class="mb-1 text-right"
                >
                   Khách trả: <span class="font-weight-bold">{{
                    formatAmount(getBuyerPaidAmount(row))
                  }}</span
                  >
                </p>

                <p
                  v-if="getOutstandingAmount(row) != null && getOutstandingAmount(row) > 0"
                  class="mb-0 text-right text-danger"
                >
                   Còn thiếu: <span class="font-weight-bold">{{
                    formatAmount(getOutstandingAmount(row))
                  }}</span
                  >
                </p>
                 <span
                  v-if="
                    getOrderTotalAmount(row) == null &&
                    getBuyerPaidAmount(row) == null &&
                    getOutstandingAmount(row) == null
                  "
                  class="text-muted"
                  >-</span
                >
              </div>

            </td>

            <td class="text-center">

              <div
                v-if="hasActions(row)"
                class="d-flex justify-content-center order-log-actions-wrap"
              >
                 <b-dropdown
                  class="order-log-actions-dd"
                  size="sm"
                  no-caret
                  right
                  > <template #button-content
                    >                  <i class="order-log-actions-dd__icon flaticon2-settings"></i> </template
                  > <b-dropdown-item
                    v-if="canCreateShipment(row)"
                    @click="$emit('create-shipment', row)"
                    :disabled="isCreatingShipment(row.externalOrderId)"
                    > <span style="color: #3f4254; font-size: 12px"
                      > <b-spinner
                        v-if="isCreatingShipment(row.externalOrderId)"
                        small
                      ></b-spinner
                      > <i
                        v-else
                        style="font-size: 1rem"
                        class="flaticon2-box"
                      ></i
                      > &nbsp; Tạo shipment (create-shipment) </span
                    > </b-dropdown-item
                  > <b-dropdown-item
                    v-if="canConfirmShipOrder(row)"
                    @click="$emit('confirm-ship-order', row)"
                    :disabled="isCreatingShipment(row.externalOrderId)"
                    > <span style="color: #3f4254; font-size: 12px"
                      > <b-spinner
                        v-if="isCreatingShipment(row.externalOrderId)"
                        small
                      ></b-spinner
                      > <i
                        v-else
                        style="font-size: 1rem"
                        class="flaticon2-check-mark"
                      ></i
                      > &nbsp; Xác nhận ship_order </span
                    > </b-dropdown-item
                  > <b-dropdown-item
                    v-if="canPrintTracking(row)"
                    @click="$emit('print-tracking', row)"
                    :disabled="isPrintingTracking(row.externalOrderId)"
                    > <span style="color: #3f4254; font-size: 12px"
                      > <b-spinner
                        v-if="isPrintingTracking(row.externalOrderId)"
                        small
                      ></b-spinner
                      > <i
                        v-else
                        style="font-size: 1rem"
                        class="flaticon2-print"
                      ></i
                      > &nbsp; In mã vận đơn </span
                    > </b-dropdown-item
                  > <b-dropdown-item
                    v-if="canPrintTracking(row)"
                    @click="$emit('print-tracking-pdf', row)"
                    :disabled="isPrintingPdf(row.externalOrderId)"
                    > <span style="color: #3f4254; font-size: 12px"
                      > <b-spinner
                        v-if="isPrintingPdf(row.externalOrderId)"
                        small
                      ></b-spinner
                      > <i
                        v-else
                        style="font-size: 1rem"
                        class="flaticon2-file"
                      ></i
                      > &nbsp; PDF phiếu giao (Shopee) </span
                    > </b-dropdown-item
                  > <b-dropdown-item
                    v-if="canSyncStatusToShopee(row)"
                    @click="$emit('sync-status-to-shopee', row)"
                    :disabled="isSyncingStatus(row.orderId)"
                    > <span style="color: #3f4254; font-size: 12px"
                      > <b-spinner
                        v-if="isSyncingStatus(row.orderId)"
                        small
                      ></b-spinner
                      > <i
                        v-else
                        style="font-size: 1rem"
                        class="flaticon2-arrow-up"
                      ></i
                      > &nbsp; Đã đóng gói </span
                    > </b-dropdown-item
                  > <b-dropdown-item
                    v-if="canChangeInternalOrderStatus(row) && checkPermissions(['ORDER_UPDATE'])"
                    @click="$emit('change-internal-order-status', row)"
                    > <span style="color: #3f4254; font-size: 12px"
                      > <i
                        style="font-size: 1rem"
                        class="fas fa-sync-alt icon-color"
                      ></i
                      > &nbsp; Đổi trạng thái </span
                    > </b-dropdown-item
                  > <b-dropdown-item
                    v-if="canSyncOneOrder(row)"
                    @click="$emit('sync-one-order', row)"
                    :disabled="isSyncingOneOrder(row.externalOrderId)"
                    > <span style="color: #3f4254; font-size: 12px"
                      > <b-spinner
                        v-if="isSyncingOneOrder(row.externalOrderId)"
                        small
                      ></b-spinner
                      > <i
                        v-else
                        style="font-size: 1rem"
                        class="flaticon2-refresh"
                      ></i
                      > &nbsp; Sync đơn hàng </span
                    > </b-dropdown-item
                  > <b-dropdown-item
                    v-if="row.syncStatus === 'failed'"
                    @click="$emit('retry', row)"
                    :disabled="isRetrying(row.id)"
                    > <span style="color: #3f4254; font-size: 12px"
                      > <b-spinner
                        v-if="isRetrying(row.id)"
                        small
                      ></b-spinner
                      > <i
                        v-else
                        style="font-size: 1rem"
                        class="flaticon2-refresh"
                      ></i
                      > &nbsp; Đồng bộ lại </span
                    > </b-dropdown-item
                  > <b-dropdown-item @click="$emit('create-complaint', row)"
                    > <span style="color: #3f4254; font-size: 12px"
                      > <i
                        style="font-size: 1rem"
                        class="flaticon2-exclamation icon-color"
                      ></i
                      > &nbsp; Tạo phiếu khiếu nại </span
                    > </b-dropdown-item
                  > <b-dropdown-item
                    v-if="row.errorMessage"
                    @click="$emit('view-error', row)"
                    > <span style="color: #3f4254; font-size: 12px"
                      > <i
                        style="font-size: 1rem"
                        class="flaticon2-information"
                      ></i
                      > &nbsp; Chi tiết </span
                    > </b-dropdown-item
                  > <b-dropdown-item
                    v-if="canViewOrderDetail(row)"
                    @click="$emit('view-order-detail', row)"
                    > <span style="color: #3f4254; font-size: 12px"
                      > <i
                        style="font-size: 1rem"
                        class="flaticon2-check-mark"
                      ></i
                      > &nbsp; Hoàn thành đơn hàng </span
                    > </b-dropdown-item
                  > </b-dropdown
                >
              </div>

            </td>

          </tr>

        </tbody>

      </table>

    </div>

  </div>

</template>

<script>
  import StatusBadge from '../shared/StatusBadge.vue'
  import ProductImage from '@/view/components/products/ProductImage.vue'
  import { ORDER_LOG_FIELDS, EXTERNAL_STATUS_BADGE_MAP } from '../constants'
  import * as ENUM from '@/utils/enum'
  import * as ENUM_NAME from '@/utils/enum-name'
  import {
    getOrderStatusBadgeClass,
    getOrderStatusLabel,
  } from '@/utils/enum-name'
  import { CLIENT_DATE_TIME } from '@/utils/date'
  import { convertPrice } from '@/utils/common'
  import { checkPermissions } from '@/utils/saveDataToLocal'
  const moment = require('moment')

  export default {
    name: 'OrderLogTable',
    components: {
      StatusBadge,
      ProductImage,
    },
    props: {
      items: {
        type: Array,
        default: () => [],
      },
      loading: {
        type: Boolean,
        default: false,
      },
      retryingIds: {
        type: Array,
        default: () => [],
      },
      syncingStatusIds: {
        type: Array,
        default: () => [],
      },
      printingTrackingIds: {
        type: Array,
        default: () => [],
      },
      syncingOneOrderIds: {
        type: Array,
        default: () => [],
      },
      creatingShipmentIds: {
        type: Array,
        default: () => [],
      },
      printingPdfIds: {
        type: Array,
        default: () => [],
      },
      selectedOrderSns: {
        type: Array,
        default: () => [],
      },
      complaintMap: {
        type: Object,
        default: () => ({}),
      },
      rowActionErrors: {
        type: Object,
        default: () => ({}),
      },
      syncQueueJobsByOrderSn: {
        type: Object,
        default: () => ({}),
      },
    },
    computed: {
      selectableItems() {
        return this.items.filter((item) => this.canSelectForBatch(item))
      },
      areAllSelectableChecked() {
        if (!this.selectableItems.length) return false
        return this.selectableItems.every((item) =>
          this.selectedOrderSns.includes(item.externalOrderId),
        )
      },
      isPartiallySelected() {
        if (!this.selectableItems.length) return false
        const selectedCount = this.selectableItems.filter((item) =>
          this.selectedOrderSns.includes(item.externalOrderId),
        ).length
        return selectedCount > 0 && selectedCount < this.selectableItems.length
      },
      columnCount() {
        return this.fields.length
      },
    },
    data() {
      return {
        fields: ORDER_LOG_FIELDS,
        externalStatusBadgeMap: EXTERNAL_STATUS_BADGE_MAP,
      }
    },
    methods: {
      getRowSyncErrorMessage(row) {
        if (!row) return ''
        return this.pickFirstNonEmpty([row.errorMessage, row.error_message])
      },
      dismissRowError(row) {
        const sn = row?.externalOrderId
        if (sn) {
          this.$emit('dismiss-row-error', sn)
        }
      },
      formatDateTime(date) {
        if (!date) return 'N/A'
        return moment(date).format(CLIENT_DATE_TIME)
      },
      formatAmount(value) {
        if (value === null || value === undefined) return '-'
        return convertPrice(value)
      },
      pickFirstNonEmpty(candidates = []) {
        for (const value of candidates) {
          if (typeof value === 'string' && value.trim()) return value.trim()
        }
        return ''
      },
      getRawItemList(logItem = {}) {
        const raw = this.parseRawOrderData(logItem)
        const list = raw?.item_list || raw?.itemList || []
        return Array.isArray(list) ? list : []
      },
      /** Dòng tương ứng trong item_list (rawOrderData), ưu tiên theo index rồi theo id dòng */
      resolveRawItemListEntry(logItem, orderItem, index) {
        const rawItems = this.getRawItemList(logItem)
        if (!rawItems.length) return null
        if (rawItems[index]) return rawItems[index]
        const keys = [
          orderItem?.orderItemId,
          orderItem?.order_item_id,
          orderItem?.id,
          orderItem?.externalOrderItemId,
        ].filter((k) => k != null && k !== '')
        for (const k of keys) {
          const found = rawItems.find(
            (r) =>
              r?.order_item_id === k ||
              r?.item_id === k ||
              String(r?.order_item_id) === String(k) ||
              String(r?.item_id) === String(k),
          )
          if (found) return found
        }
        return null
      },
      /** Tên trưng bày: chỉ từ item_list trong rawOrderData (Shopee) */
      getDisplayNameFromItemList(logItem, orderItem, index) {
        const rawItem = this.resolveRawItemListEntry(logItem, orderItem, index)
        if (!rawItem) return ''
        const itemName = this.pickFirstNonEmpty([rawItem.item_name, rawItem.itemName])
        const modelName = this.pickFirstNonEmpty([rawItem.model_name, rawItem.modelName])
        const joined = [itemName, modelName].filter(Boolean).join(' - ')
        return (joined || itemName || modelName || '').trim()
      },
      /** Tên hệ thống: chỉ từ orderItems (đồng bộ ERP) */
      getSystemNameFromOrderItems(logItem, orderItem, index) {
        const items = logItem.orderItems
        if (!Array.isArray(items) || items.length === 0) return ''
        let oi = items[index]
        if (!oi && orderItem) {
          const keys = [orderItem.id, orderItem.orderItemId, orderItem.order_item_id].filter(
            (k) => k != null && k !== '',
          )
          oi = items.find((x) =>
            keys.some(
              (k) =>
                x?.id === k ||
                x?.orderItemId === k ||
                String(x?.id) === String(k) ||
                String(x?.orderItemId) === String(k),
            ),
          )
        }
        if (!oi) return ''
        return this.pickFirstNonEmpty([
          oi.systemProductName,
          oi.internalProductName,
          oi.erpProductName,
          oi.productName,
        ])
      },
      getProductNamesTooltip(logItem, orderItem, index) {
        const d = this.getDisplayNameFromItemList(logItem, orderItem, index) || '—'
        const s = this.getSystemNameFromOrderItems(logItem, orderItem, index) || '—'
        return `Tên trưng bày: ${d}\nTên hệ thống: ${s}`
      },
      getSecondaryProductLine(orderItem = {}) {
        if (orderItem.imeiCode && String(orderItem.imeiCode).trim()) {
          return `IMEI: ${String(orderItem.imeiCode).trim()}`
        }
        return this.pickFirstNonEmpty([orderItem.productBarCode, orderItem.productCode]) || ''
      },
      getOrderItemInStockQuantity(orderItem = {}) {
        if (orderItem.inStockQuantity === undefined || orderItem.inStockQuantity === null) {
          return null
        }
        const n = Number(orderItem.inStockQuantity)
        return Number.isFinite(n) ? n : null
      },
      getBuyerName(item = {}) {
        const raw = this.parseRawOrderData(item) || {}
        return (
          this.pickFirstNonEmpty([
            item.buyerName,
            item.customerName,
            item.recipientName,
            raw.recipient_address?.name,
            raw.recipientAddress?.name,
            raw.buyer_username,
            raw.buyerUsername,
          ]) || ''
        )
      },
      getBuyerPhone(item = {}) {
        const raw = this.parseRawOrderData(item) || {}
        return (
          this.pickFirstNonEmpty([
            item.buyerPhone,
            item.customerPhone,
            item.recipientPhone,
            raw.recipient_address?.phone,
            raw.recipientAddress?.phone,
          ]) || ''
        )
      },
      /** Chỉ hiển thị khi có nội dung thật (không phải toàn * / ẩn danh). */
      isRecipientAddressPlaceholder(str) {
        if (str == null || str === '') return true
        const t = String(str).trim()
        if (!t) return true
        if (t === '****') return true
        if (/^[*•·\s]+$/.test(t)) return true
        return false
      },
      /**
       * Địa chỉ giao hàng từ rawOrderData (Shopee: recipient_address), ưu tiên full_address rồi ghép phụ lục.
       */
      getRecipientShippingAddress(item = {}) {
        const raw = this.parseRawOrderData(item) || {}
        const addr = raw.recipient_address || raw.recipientAddress
        if (!addr || typeof addr !== 'object') {
          const line = this.pickFirstNonEmpty([
            item.shippingAddress,
            item.shipping_address,
            item.recipientAddressLine,
            item.deliveryAddress,
          ])
          return line && !this.isRecipientAddressPlaceholder(line) ? line : ''
        }
        const fullRaw = addr.full_address ?? addr.fullAddress ?? ''
        if (fullRaw && !this.isRecipientAddressPlaceholder(fullRaw)) {
          return String(fullRaw).trim()
        }
        const segments = [
          addr.address,
          addr.town,
          addr.district,
          addr.city,
          addr.state,
          addr.region,
          addr.zipcode,
        ]
          .map((s) => (s != null && String(s).trim() ? String(s).trim() : ''))
          .filter((s) => s && !this.isRecipientAddressPlaceholder(s))
        const seen = new Set()
        const uniq = []
        for (const s of segments) {
          const k = s.toLowerCase()
          if (!seen.has(k)) {
            seen.add(k)
            uniq.push(s)
          }
        }
        if (uniq.length) {
          return uniq.join(', ')
        }
        return ''
      },
      getShopDisplayName(item = {}) {
        const name = this.pickFirstNonEmpty([
          item.marketplaceConnection && item.marketplaceConnection.shopName,
          item.shopName,
          item.platformName,
          item.marketplaceConnection && item.marketplaceConnection.platform,
        ])
        return name || '-'
      },
      getOrderNotesPayload(item = {}) {
        const n = item.orderNotes ?? item.order_notes
        if (!n || typeof n !== 'object') return null
        return n
      },
      /**
       * Các dòng ghi chú từ đơn hàng nội bộ (UpsertOrder / DB), chỉ trả field có nội dung.
       */
      getOrderNotesEntries(item = {}) {
        const n = this.getOrderNotesPayload(item)
        if (!n) return []
        const defs = [
          { keys: ['saleNote', 'sale_note'], label: 'Ghi chú bán hàng' },
          { keys: ['customerNote', 'customer_note'], label: 'Ghi chú khách hàng' },
          { keys: ['warrantyNote', 'warranty_note'], label: 'Ghi chú bảo hành' },
          { keys: ['creditFeeNote', 'credit_fee_note'], label: 'Ghi chú phí tín dụng' },
        ]
        const out = []
        for (const { keys, label } of defs) {
          let raw = ''
          for (const k of keys) {
            const v = n[k]
            if (v != null && String(v).trim()) {
              raw = String(v).trim()
              break
            }
          }
          if (raw) out.push({ label, text: raw })
        }
        return out
      },
      parseMoneyField(candidates = []) {
        for (const c of candidates) {
          if (c === null || c === undefined || c === '') continue
          const n = Number(c)
          if (!Number.isNaN(n)) return n
        }
        return null
      },
      getOrderTotalAmount(item = {}) {
        const raw = this.parseRawOrderData(item) || {}
        return this.parseMoneyField([
          item.totalAmount,
          item.orderTotalAmount,
          raw.total_amount,
          raw.totalAmount,
        ])
      },
      getBuyerPaidAmount(item = {}) {
        const raw = this.parseRawOrderData(item) || {}
        return this.parseMoneyField([
          item.buyerPaidAmount,
          raw.buyer_paid_amount,
          raw.buyerPaidAmount,
        ])
      },
      getOutstandingAmount(item = {}) {
        const total = this.getOrderTotalAmount(item)
        const paid = this.getBuyerPaidAmount(item)
        if (total != null && paid != null) return Math.max(0, total - paid)
        return null
      },
      parseRawOrderData(item) {
        const raw = item?.rawOrderData
        if (!raw) return null
        if (typeof raw === 'object') return raw
        if (typeof raw === 'string') {
          try {
            return JSON.parse(raw)
          } catch (e) {
            return null
          }
        }
        return null
      },
      getOrderItems(item = {}) {
        if (Array.isArray(item.orderItems) && item.orderItems.length > 0) {
          return item.orderItems
        }

        const rawOrderData = this.parseRawOrderData(item)
        const rawItems = rawOrderData?.item_list || rawOrderData?.itemList || []
        if (!Array.isArray(rawItems) || rawItems.length === 0) return []

        return rawItems.map((rawItem, index) => {
          const quantity =
            Number(
              rawItem?.model_quantity_purchased ??
                rawItem?.model_quantity ??
                rawItem?.quantity ??
                1,
            ) || 1

          const unitPrice =
            Number(
              rawItem?.model_discounted_price ??
                rawItem?.item_price ??
                rawItem?.model_original_price ??
                0,
            ) || 0

          const totalAmount =
            Number(rawItem?.totalAmount ?? rawItem?.total_amount ?? unitPrice * quantity) || 0

          const itemName = this.pickFirstNonEmpty([rawItem?.item_name, rawItem?.itemName])
          const modelName = this.pickFirstNonEmpty([rawItem?.model_name, rawItem?.modelName])
          const displayName = [itemName, modelName].filter(Boolean).join(' - ')

          return {
            id: rawItem?.order_item_id || rawItem?.item_id || `${item.externalOrderId}-${index}`,
            productBarCode: rawItem?.barcode || rawItem?.item_barcode || '',
            productCode: rawItem?.model_sku || rawItem?.item_sku || rawItem?.itemSku || '',
            productName: displayName || itemName || modelName || '',
            systemProductName: displayName || itemName || modelName || '',
            displayProductName: displayName || itemName || modelName || '',
            quantity,
            productPrice: unitPrice,
            totalAmount,
            imageUrl: rawItem?.image_info?.image_url || rawItem?.imageUrl || null,
          }
        })
      },
      getSystemOrderItems(item = {}) {
        return Array.isArray(item.orderItems) ? item.orderItems : []
      },
      getDisplayOrderItems(item = {}) {
        const systemItems = this.getSystemOrderItems(item)
        if (systemItems.length > 0) return systemItems
        return this.getOrderItems(item)
      },
      getOrderPlacedAt(item) {
        const rawOrderData = this.parseRawOrderData(item)
        const createdTime =
          rawOrderData?.create_time ?? rawOrderData?.createTime ?? item?.orderCreateTime ?? null

        if (createdTime !== null && createdTime !== undefined && createdTime !== '') {
          const timestamp = Number(createdTime)
          if (!Number.isNaN(timestamp) && timestamp > 0) {
            return moment.unix(timestamp).format(CLIENT_DATE_TIME)
          }
        }

        return '-'
      },
      normalizeShippingValue(value) {
        if (value === null || value === undefined) return ''
        if (typeof value === 'string') return value.trim()
        if (typeof value === 'number') return String(value)
        if (typeof value === 'object') {
          return (
            value?.name ||
            value?.label ||
            value?.channel_name ||
            value?.channelName ||
            value?.carrier_name ||
            value?.carrierName ||
            ''
          )
        }
        return ''
      },
      normalizeTrackingValue(value) {
        if (value === null || value === undefined) return ''
        if (typeof value === 'string') return value.trim()
        if (typeof value === 'number') return String(value)
        if (Array.isArray(value)) {
          for (const item of value) {
            const normalized = this.normalizeTrackingValue(item)
            if (normalized) return normalized
          }
          return ''
        }
        if (typeof value === 'object') {
          return (
            value?.trackingNumber ||
            value?.tracking_no ||
            value?.tracking_number ||
            value?.waybillNo ||
            value?.waybill_no ||
            value?.codeShip ||
            value?.orderCodeShip ||
            value?.order_code_ship ||
            ''
          )
        }
        return ''
      },
      findTrackingDeep(source, maxDepth = 6, depth = 0) {
        if (!source || depth > maxDepth) return ''
        if (typeof source === 'string' || typeof source === 'number') {
          return this.normalizeTrackingValue(source)
        }
        if (Array.isArray(source)) {
          for (const item of source) {
            const found = this.findTrackingDeep(item, maxDepth, depth + 1)
            if (found) return found
          }
          return ''
        }
        if (typeof source !== 'object') return ''

        for (const [key, value] of Object.entries(source)) {
          const keyLower = key.toLowerCase()
          if (
            keyLower.includes('tracking') ||
            keyLower.includes('waybill') ||
            keyLower.includes('codeship') ||
            keyLower === 'sn'
          ) {
            const direct = this.normalizeTrackingValue(value)
            if (direct) return direct
          }
        }

        for (const value of Object.values(source)) {
          const found = this.findTrackingDeep(value, maxDepth, depth + 1)
          if (found) return found
        }

        return ''
      },
      getTrackingNumber(item) {
        const rawOrderData = this.parseRawOrderData(item)
        const candidates = [
          item?.orderCodeShip,
          item?.order_code_ship,
          item?.trackingNumber,
          item?.tracking_number,
          item?.trackingNo,
          item?.waybillNo,
          item?.waybill_number,
          item?.shipmentNo,
          item?.shippingTrackingNumber,
          item?.shipping_tracking_number,
          item?.shipping?.trackingNumber,
          item?.shipping?.tracking_number,
          item?.delivery?.trackingNumber,
          item?.delivery?.tracking_number,
          item?.codeShip,
          item?.shippingInfo,
          item?.shippingInformation,
          item?.deliveryInfo,
          item?.logistics,
          rawOrderData?.orderCodeShip,
          rawOrderData?.order_code_ship,
          rawOrderData?.tracking_number,
          rawOrderData?.tracking_no,
          rawOrderData?.trackingNumber,
          rawOrderData?.waybill_no,
          rawOrderData?.waybillNo,
          rawOrderData?.waybill_number,
          rawOrderData?.codeShip,
          rawOrderData?.shipping_parameter_hint?.tracking_number,
          rawOrderData?.shipping_parameter_hint?.trackingNumber,
          rawOrderData?.logistics?.tracking_number,
          rawOrderData?.logistics?.trackingNumber,
          rawOrderData?.shipping_info,
          rawOrderData?.shippingInfo,
          rawOrderData?.package_list,
          rawOrderData?.packageList,
        ]
        for (const value of candidates) {
          const normalized = this.normalizeTrackingValue(value)
          if (normalized) return normalized
        }
        return this.findTrackingDeep(rawOrderData)
      },
      getShippingUnit(item) {
        const rawOrderData = this.parseRawOrderData(item)

        const candidates = [
          item?.shippingUnit,
          item?.logisticsChannel,
          item?.shippingCarrier,
          item?.shippingProvider,
          rawOrderData?.shipping_unit,
          rawOrderData?.shippingUnit,
          rawOrderData?.logistics_channel,
          rawOrderData?.logisticsChannel,
          rawOrderData?.shipping_carrier,
          rawOrderData?.shippingCarrier,
          rawOrderData?.shipping_carrier_name,
          rawOrderData?.shipping_provider,
          rawOrderData?.shipping_provider_name,
          rawOrderData?.fulfillment_channel,
          rawOrderData?.pickup?.channel_name,
          rawOrderData?.pickup?.channelName,
        ]

        for (const value of candidates) {
          const normalized = this.normalizeShippingValue(value)
          if (normalized) {
            return normalized
          }
        }

        return '-'
      },
      getSyncQueueEntry(row) {
        const sn = row?.externalOrderId
        if (!sn || !this.syncQueueJobsByOrderSn) return null
        return this.syncQueueJobsByOrderSn[String(sn)] || null
      },
      getSyncQueueLabel(row) {
        const e = this.getSyncQueueEntry(row)
        const st = e?.state
        if (st === 'active') return 'Đang đồng bộ (queue)'
        if (st === 'waiting') return 'Chờ đồng bộ'
        if (st === 'delayed') return 'Tạm hoãn đồng bộ'
        return st ? `Đồng bộ (${st})` : ''
      },
      getSyncQueueBadgeVariant(row) {
        const st = this.getSyncQueueEntry(row)?.state
        if (st === 'active') return 'primary'
        if (st === 'waiting') return 'info'
        if (st === 'delayed') return 'warning'
        return 'secondary'
      },
      shouldShowSyncSpinner(row) {
        return !!(this.getSyncQueueEntry(row) || this.isSyncingOneOrder(row?.externalOrderId))
      },
      getSyncQueueSpinnerVariant(row) {
        if (!this.getSyncQueueEntry(row) && this.isSyncingOneOrder(row?.externalOrderId)) {
          return 'primary'
        }
        return this.getSyncQueueBadgeVariant(row)
      },
      getSyncQueueSpinnerCaption(row) {
        if (this.getSyncQueueEntry(row)) return this.getSyncQueueLabel(row)
        if (this.isSyncingOneOrder(row?.externalOrderId)) return 'Đang đồng bộ…'
        return ''
      },
      isRetrying(id) {
        return this.retryingIds.includes(id)
      },
      isSyncingStatus(orderId) {
        return this.syncingStatusIds.includes(orderId)
      },
      isPrintingTracking(orderSn) {
        return this.printingTrackingIds.includes(orderSn)
      },
      isSyncingOneOrder(orderSn) {
        return this.syncingOneOrderIds.includes(orderSn)
      },
      isCreatingShipment(orderSn) {
        return this.creatingShipmentIds.includes(orderSn)
      },
      isPrintingPdf(orderSn) {
        return this.printingPdfIds.includes(orderSn)
      },
      hasActions(item) {
        return (
          item.syncStatus === 'failed' ||
          item.errorMessage ||
          this.canPrintTracking(item) ||
          this.canSyncStatusToShopee(item) ||
          this.canChangeInternalOrderStatus(item) ||
          this.canSyncOneOrder(item) ||
          this.canViewOrderDetail(item) ||
          true
        )
      },
      checkPermissions,
      canChangeInternalOrderStatus(item) {
        return !!item?.orderId
      },
      canSyncOneOrder(item) {
        return item.externalOrderId && item.marketplaceConnectionId
      },
      canCreateShipment(item) {
        const st = item.externalStatus
        return (
          item.syncStatus === 'synced' &&
          item.externalOrderId &&
          item.marketplaceConnectionId &&
          (st === 'READY_TO_SHIP' ||
            st === 'RETRY_SHIP' ||
            st === 'PROCESSED')
        )
      },
      canConfirmShipOrder(item) {
        const st = item.externalStatus
        return (
          item.syncStatus === 'synced' &&
          item.externalOrderId &&
          item.marketplaceConnectionId &&
          (st === 'READY_TO_SHIP' || st === 'RETRY_SHIP')
        )
      },
      canPrintTracking(item) {
        return (
          item.syncStatus === 'synced' &&
          item.externalOrderId &&
          (item.externalStatus === 'READY_TO_SHIP' ||
            item.externalStatus === 'PROCESSED' ||
            item.externalStatus === 'RETRY_SHIP')
        )
      },
      canSelectForBatch(item) {
        return !!item.externalOrderId
      },
      isSelected(orderSn) {
        return this.selectedOrderSns.includes(orderSn)
      },
      toggleRowSelection(item, checked) {
        if (!this.canSelectForBatch(item)) return
        this.$emit('toggle-select-order', {
          orderSn: item.externalOrderId,
          checked: !!checked,
        })
      },
      toggleSelectAllOnPage(checked) {
        const orderSns = this.selectableItems.map((item) => item.externalOrderId).filter(Boolean)
        this.$emit('toggle-select-all', {
          orderSns,
          checked: !!checked,
        })
      },
      canViewOrderDetail(item) {
        return (
          item.syncStatus === 'synced' &&
          item.orderId &&
          (item.externalStatus === 'SHIPPED' || item.externalStatus === 'TO_CONFIRM_RECEIVE')
        )
      },
      canSyncStatusToShopee(item) {
        return (
          item.syncStatus === 'synced' && item.orderId && item.externalStatus === 'READY_TO_SHIP'
        )
      },
      getOrderItemImageUrl(logItem, orderItem, index) {
        const fromOrderItems = this.pickFirstNonEmpty([
          orderItem?.imageUrl,
          orderItem?.image_url,
          orderItem?.thumbnailUrl,
          orderItem?.thumbnail,
          typeof orderItem?.product === 'object' && orderItem.product != null
            ? this.pickFirstNonEmpty([
                orderItem.product.imageUrl,
                orderItem.product.image_url,
                orderItem.product.thumbnailUrl,
              ])
            : '',
        ])
        if (fromOrderItems) return fromOrderItems

        const rawItem = this.resolveRawItemListEntry(logItem, orderItem, index)
        if (!rawItem) return null

        const info = rawItem.image_info || rawItem.imageInfo
        return (
          this.pickFirstNonEmpty([
            typeof info === 'object' && info ? info.image_url : '',
            typeof info === 'object' && info ? info.imageUrl : '',
            rawItem.imageUrl,
            rawItem.image_url,
          ]) || null
        )
      },
      getOrderStatusText(item) {
        const status = Number(item.orderStatus)
        return getOrderStatusLabel(status, item.orderStatusName)
      },
      getComplaintInfo(item) {
        const key = item.externalOrderId || item.platformOrderId
        return key ? this.complaintMap[key] : null
      },
      complaintTicketId(row) {
        const info = this.getComplaintInfo(row)
        return info && info.id != null ? info.id : null
      },
      complaintTicketRoute(row) {
        const info = this.getComplaintInfo(row)
        const q = {}
        if (info && info.id != null) q.complaintId = info.id
        const platformOrderId = row.platformOrderId || row.externalOrderId
        if (platformOrderId) q.platformOrderId = platformOrderId
        return { name: 'platform-integration-complaints', query: q }
      },
      bindClassNameStatus(item) {
        return getOrderStatusBadgeClass(Number(item.orderStatus))
      },
    },
  }
</script>

<style lang="scss" scoped>
  .order-log-table-wrap {
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
}

.order-log-table-shell {
  .order-log-table {
    width: 100%;
    table-layout: fixed;
  }

  .order-log-table th,
  .order-log-table td {
    padding: 0.75rem;
    vertical-align: middle;
    border-top: 1px solid #ecf0f3;
    white-space: normal;
    word-break: break-word;
    overflow-wrap: anywhere;
  }

  .order-log-table th:nth-child(2),
  .order-log-table td:nth-child(2) {
    white-space: nowrap;
  }

  .order-log-table thead th {
    font-weight: 600;
    color: #3f4254;
    background-color: #f3f6f9;
  }

  .order-log-meta-cell {
    padding: 0.65rem 1rem !important;
    border-bottom: none !important;
    background: linear-gradient(180deg, #f9fafb 0%, #f3f6f9 100%);
    border-left: 3px solid #e1e3ea;
  }

  .order-meta-express-badge {
    font-size: 11px;
    font-weight: 600;
    line-height: 1.2;
    padding: 0.2em 0.55em;
  }
}

.order-log-table {
  background: white;
  border-radius: 4px;
  width: 100%;
  min-width: 100%;

  :deep(th) {
    white-space: normal;
  }
}

.product-name-separator {
  margin: 0.35rem 0;
  border: 0;
  border-top: 1px solid #e4e6ef;
}

.order-info-list li {
  margin-bottom: 6px;
  line-height: 1.45;
}

.order-info-list li:last-child {
  margin-bottom: 0;
}

.text-price {
  font-size: 12px;
}

.product-name-value--primary {
  font-size: 13px;
  line-height: 1.35;
  color: #3f4254;
}

.store-location-text {
  color: #3f4254;
  font-size: 12px;
  letter-spacing: 0.02em;
}

.order-id-link {
  color: #3699ff;
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
    color: #0073e6;
  }
}

.order-items-cell {
  .order-item-row {
    padding: 4px 0;
    border-bottom: 1px solid #f1f1f1;

    &:last-child {
      border-bottom: none;
    }
  }

  .product-name-block {
    min-width: 0;
  }

  .product-name-label {
    font-size: 11px;
    line-height: 1.2;
    color: #7e8299;
  }

  .product-name-value {
    color: #3f4254;
    font-weight: 500;
    display: block;
    overflow: visible;
    min-height: 0;
    white-space: normal;
    word-break: break-word;
    overflow-wrap: anywhere;
  }

  .product-name-value--system {
    font-size: 13px;
    line-height: 1.3;
    max-height: none;
    overflow: visible;
    display: block;
  }

  .product-name-value--display {
    font-size: 12px;
    line-height: 1.3;
    max-height: none;
    overflow: visible;
    display: block;
  }
}

.badge-dark-blue {
  background-color: #1861ad;
}

.return-order-link {
  color: #3699ff;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
    color: #0073e6;
  }
}

.issue-info-cell {
  min-width: 0;
}

.order-time-cell {
  min-width: 0;
  line-height: 1.35;
}

.order-log-error-row .order-log-error-cell,
.order-log-sync-error-row .order-log-error-cell {
  padding: 0.5rem 1rem 0.75rem !important;
  border-top: none !important;
  background-color: #f7f8fa;
}

.order-log-sync-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin: 0;
  padding: 0.75rem 1rem 0.8rem;
  border-radius: 6px;
  border: 1px solid #e5dcc4;
  background: linear-gradient(145deg, #fffcf5 0%, #fff6e8 50%, #fff1dc 100%);
  box-shadow: 0 1px 2px rgba(63, 66, 84, 0.06);
  font-size: 0.8125rem;
  line-height: 1.5;
  color: #4a3f2a;
  letter-spacing: 0.01em;
}

.order-log-sync-banner__icon {
  flex-shrink: 0;
  margin-top: 0.12rem;
  color: #b8860b;
  font-size: 1rem;
  opacity: 0.9;
}

.order-log-sync-banner__text {
  flex: 1;
  min-width: 0;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.order-log-inline-error {
  border: 1px solid #f1aeb5;
  border-radius: 6px;
  position: relative;
  background: #fff5f5 !important;
  box-shadow: 0 1px 2px rgba(63, 66, 84, 0.05);
  color: #842029 !important;
  font-size: 0.8125rem;
  line-height: 1.5;
  letter-spacing: 0.01em;

  > i {
    color: #dc3545;
    opacity: 1;
  }

  span {
    color: #842029;
  }
}

.order-log-error-close {
  font-size: 1.35rem;
  line-height: 1;
  padding: 0;
  margin: -0.15rem 0 0;
  color: #842029 !important;
  opacity: 0.75;
  text-shadow: none;
}

.order-log-error-close:hover {
  opacity: 1;
  color: #58151c !important;
}

.status-combined-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  padding: 0.15rem 0;
}

.order-log-sync-queue-block {
  width: 100%;
  max-width: 168px;
  align-self: stretch;
}

.order-log-sync-spinner {
  width: 1.1rem !important;
  height: 1.1rem !important;
}

.order-log-sync-queue-caption {
  color: #5e6278;
  font-size: 0.68rem;
  line-height: 1.35;
  text-align: center;
  font-weight: 600;
}

.status-combined-cell :deep(.badge) {
  font-weight: 600;
  font-size: 0.7rem;
  letter-spacing: 0.03em;
  padding: 0.35em 0.65em;
}

.order-log-sync-time {
  font-size: 0.72rem;
  opacity: 0.9;
  max-width: 100%;
  text-align: center;
}

.order-log-actions-wrap {
  :deep(.order-log-actions-dd > .btn) {
    min-width: 2.1rem;
    min-height: 2.1rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #e4e6ef;
    border-radius: 6px;
    background: #fff;
    color: #7e8299;
    box-shadow: 0 1px 0 rgba(63, 66, 84, 0.04);
    transition:
      border-color 0.15s ease,
      background 0.15s ease,
      color 0.15s ease,
      box-shadow 0.15s ease;
  }

  :deep(.order-log-actions-dd > .btn:hover),
  :deep(.order-log-actions-dd > .btn:focus) {
    border-color: #3699ff;
    background: #f3f8ff;
    color: #187de4;
    box-shadow: 0 0 0 0.2rem rgba(54, 153, 255, 0.12);
  }
}

.order-log-actions-dd__icon {
  font-size: 1rem;
  line-height: 1;
  padding: 0;
}

.shipping-info-cell {
  min-width: 0;
  line-height: 1.35;
}

.buyer-info-cell {
  min-width: 0;
  max-width: 100%;
}

.recipient-shipping-address {
  border-top: 1px solid #ebedf3;
}
</style>

